import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import TopUpWalletModal from "../components/TopUpWalletModal"; // ✅ Make sure modal is in components
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

// ✅ Background image for wallet card
import walletBg from "../assets/wallet-bg.jpg"; // 👉 Add your image to assets folder

export default function WalletPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);

  // ✅ Example Wallet Balance
  const walletBalance = 12; // Units

  // ✅ Mock Transaction History (simulate more for pagination)
  const transactions = Array.from({ length: 35 }, (_, i) => ({
    id: i + 1,
    type: i % 2 === 0 ? "credit" : "debit",
    status: i % 2 === 0 ? "Wallet Top-Up" : "Guest Check-In",
    amount: i % 2 === 0 ? 5000 : -1000,
    date: `Jul ${30 - (i % 28)}, 2025`,
  }));

  // ✅ Pagination Setup
  const perPage = 8;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(transactions.length / perPage);
  const displayedTransactions = transactions.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="d-flex vh-100 overflow-hidden">
      {/* ✅ Sidebar */}
      <Sidebar
        active="wallet"
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* ✅ Main Content */}
      <div className="flex-grow-1 d-flex flex-column bg-light">
        <Header 
          darkMode={darkMode} 
          setDarkMode={setDarkMode} 
          toggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />

        {/* ✅ Page Content */}
        <main className="flex-grow-1 overflow-auto p-4">

          {/* ✅ Wallet Balance Card with background image */}
          <div 
            className="card shadow-sm border-0 mb-4 text-center text-white"
            style={{
              backgroundImage: `url(${walletBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "relative",
              overflow: "hidden"
            }}
          >
            {/* 🔵 Overlay */}
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.4)"
            }}></div>

            {/* 🔵 Wallet Info */}
            <div className="card-body position-relative" style={{ zIndex: 2 }}>
              <h5 className="mb-2 cool-font text-white">Wallet Balance</h5>
              <h1 className="fw-bold">{walletBalance} Units</h1>
              <p className="mb-3">₦{walletBalance * 1000} Naira Equivalent</p>

              {/* 🔵 Top Up Button */}
              <button 
                className="btn btn-light btn-lg rounded fs-6"
                onClick={() => setShowTopUpModal(true)}
              >
                + Top Up Wallet
              </button>
            </div>
          </div>

          {/* ✅ Transaction History Table */}
          <div className="card border-0">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="fw-bold m-0">Transaction History</h5>
              <span className="text-muted small">Last 30 Days</span>
            </div>

            <div className="table-responsive py-2">
              <table className="table table-hover align-middle mb-0 ">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }}></th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTransactions.map((tx) => (
                    <tr key={tx.id}>
                      {/* 🔵 Icon */}
                      <td>
                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center`}
                          style={{
                            width: "36px",
                            height: "36px",
                            background: tx.type === "credit" ? "rgba(25, 135, 84, 0.1)" : "rgba(220, 53, 69, 0.1)",
                          }}
                        >
                          {tx.type === "credit" ? (
                            <ArrowDownLeft size={18} className="text-success" />
                          ) : (
                            <ArrowUpRight size={18} className="text-danger" />
                          )}
                        </div>
                      </td>

                      {/* 🔵 Status */}
                      <td className="fw-bold">{tx.status}</td>

                      {/* 🔵 Amount */}
                      <td className={tx.type === "credit" ? "text-success fw-bold" : "text-danger fw-bold"}>
                        {tx.type === "credit" ? `+₦${tx.amount.toLocaleString()}` : `₦${Math.abs(tx.amount).toLocaleString()}`}
                      </td>

                      {/* 🔵 Date */}
                      <td className="text-muted">{tx.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ✅ Empty State */}
            {transactions.length === 0 && (
              <div className="text-center p-4">
                <p className="text-muted">No transactions found.</p>
              </div>
            )}

            {/* ✅ Pagination */}
            {transactions.length > perPage && (
              <div className="d-flex justify-content-center align-items-center gap-2 p-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  ‹ Prev
                </button>
                <span className="fw-bold">{page} / {totalPages}</span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next ›
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ✅ Top Up Wallet Modal */}
      {showTopUpModal && (
        <TopUpWalletModal show={showTopUpModal} onClose={() => setShowTopUpModal(false)} />

      )}
    </div>
  );
}
